import { useAppStore, type ServiceHealth } from '@callhub/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

function healthDotClass(health: ServiceHealth) {
  if (health === 'operational') return 'bg-emerald-400';
  if (health === 'degraded') return 'bg-amber-400';
  if (health === 'outage') return 'bg-red-400';
  if (health === 'maintenance') return 'bg-sky-400';
  return 'bg-muted-foreground';
}

function healthBadgeClass(health: ServiceHealth) {
  if (health === 'operational') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (health === 'degraded') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  if (health === 'outage') return 'bg-red-500/15 text-red-700 dark:text-red-300';
  if (health === 'maintenance') return 'bg-sky-500/15 text-sky-700 dark:text-sky-300';
  return 'bg-muted text-muted-foreground';
}

function healthLabel(health: ServiceHealth) {
  if (health === 'operational') return 'Operational';
  if (health === 'degraded') return 'Degraded';
  if (health === 'outage') return 'Outage';
  if (health === 'maintenance') return 'Maintenance';
  return 'Unknown';
}

function providerLabel(provider: 'cloudflare' | 'twilio' | 'callhub') {
  if (provider === 'cloudflare') return 'Cloudflare';
  if (provider === 'twilio') return 'Twilio';
  return 'CallHub';
}

export function StatusPage() {
  const isConnected = useAppStore((state) => state.isConnected);
  const serviceStatusQuery = useQuery({
    queryKey: ['service-status'],
    queryFn: async () => {
      const response = await api.getServiceStatus();
      return response.data;
    },
    staleTime: 45_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  const snapshot = serviceStatusQuery.data;
  const overallHealth: ServiceHealth = snapshot?.overallHealth ?? 'unknown';
  const generatedAtLabel = snapshot?.generatedAt
    ? new Date(snapshot.generatedAt).toLocaleString()
    : null;

  if (serviceStatusQuery.isPending) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">Loading service status...</p>
      </div>
    );
  }

  if (serviceStatusQuery.isError || !snapshot) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
        <h2 className="text-xl font-semibold text-foreground">Service Status</h2>
        <p className="mt-2 text-sm text-red-400">Service status API is unavailable right now.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Service Status</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Detailed edge runtime and carrier health chain.
          </p>
          {generatedAtLabel ? (
            <p className="mt-1 text-xs text-muted-foreground">Updated {generatedAtLabel}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background px-2 py-1 text-xs text-foreground">
            <span
              className={`h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-muted-foreground'}`}
            />
            {isConnected ? 'Connected' : 'Offline'}
          </span>
          <span className={`rounded-full px-2 py-1 text-xs ${healthBadgeClass(overallHealth)}`}>
            {healthLabel(overallHealth)}
          </span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {(
          [
            { label: 'Cloudflare', provider: snapshot.providers.cloudflare },
            { label: 'Twilio', provider: snapshot.providers.twilio },
          ] as const
        ).map(({ label, provider }) => {
          const providerStatus = provider.status;
          const globalStatus = provider.globalStatus;
          const showGlobalStatus = !!globalStatus && globalStatus !== providerStatus;
          return (
            <section key={label} className="rounded-xl border border-border bg-background/70 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${healthDotClass(providerStatus)}`} />
                  <p className="text-sm font-semibold text-foreground">{label}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs ${healthBadgeClass(providerStatus)}`}>
                  {healthLabel(providerStatus)}
                </span>
              </div>
              {showGlobalStatus ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Global: {healthLabel(globalStatus)}
                </p>
              ) : null}
              {provider.description ? (
                <p className="mt-2 text-xs text-muted-foreground">{provider.description}</p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                {provider.pageUrl ? (
                  <a
                    href={provider.pageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    Provider page
                  </a>
                ) : null}
                <a
                  href={provider.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Source
                </a>
              </div>
            </section>
          );
        })}
      </div>

      <section className="rounded-xl border border-border bg-background/70 p-3">
        <h3 className="text-lg font-medium text-foreground">Service Chain</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Ordered runtime path used by calls, messaging, AI, and retrieval.
        </p>
        <div className="mt-3 space-y-2">
          {snapshot.chain.map((stage) => (
            <div key={stage.id} className="rounded-lg border border-border bg-card px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`h-1.5 w-1.5 rounded-full ${healthDotClass(stage.status)}`} />
                  <p className="text-sm font-semibold text-foreground">{stage.label}</p>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {providerLabel(stage.provider)}
                  </span>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs ${healthBadgeClass(stage.status)}`}>
                  {healthLabel(stage.status)}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{stage.responsibility}</p>
              <p className="mt-1 text-xs text-muted-foreground">Source: {stage.source}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
